import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import Icon from "../../components/HOC/Icon";
import getIssues from "../../controllers/logger/getIssues";
import LoggerBadge from "../../components/logger/LoggerBadge";
import IssueDetailDrawer from "../../components/logger/IssueDetailDrawer";
import {
  environmentTone,
  orderLabel,
  statusCodeTone,
  statusTone,
  truncate,
} from "../../components/logger/loggerUtils";

const ENV_TABS = [
  { value: "all", label: "All" },
  { value: "production", label: "Production" },
  { value: "staging", label: "Staging" },
  { value: "development", label: "Development" },
];

export default function LogsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [draftSearch, setDraftSearch] = useState("");
  const [environment, setEnvironment] = useState("all");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState([]);
  const [meta, setMeta] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(searchParams.get("id") || null);

  const query = useMemo(
    () => ({
      page,
      limit,
      search: search || undefined,
      environment: environment !== "all" ? environment : undefined,
    }),
    [page, limit, search, environment]
  );

  const load = useCallback(async () => {
    setLoading(true);
    const res = await getIssues(query);
    setLoading(false);
    if (res.return) {
      setIssues(res.data || []);
      setMeta(res.meta || { page: 1, limit, total: 0, totalPages: 0 });
      setError("");
    } else {
      setIssues([]);
      setError(res.msg || "Unable to load issues");
    }
  }, [query, limit]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedId(searchParams.get("id") || null);
  }, [searchParams]);

  function openIssue(id) {
    setSelectedId(id);
    const next = new URLSearchParams(searchParams);
    next.set("id", id);
    setSearchParams(next, { replace: true });
  }

  function closeIssue() {
    setSelectedId(null);
    const next = new URLSearchParams(searchParams);
    next.delete("id");
    setSearchParams(next, { replace: true });
  }

  function applySearch(e) {
    e?.preventDefault?.();
    setPage(1);
    setSearch(draftSearch.trim());
  }

  function selectEnvironment(value) {
    setPage(1);
    setEnvironment(value);
  }

  return (
    <div className="w-full px-4 lg:px-10 py-8 flex flex-col flex-1 font-main bg-[#F8F8F8] min-h-full">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-6">
        <div>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-1 text-sm text-[#64748B] hover:text-primary1 mb-2"
          >
            <Icon icon="solar:arrow-left-linear" className="!h-4 !w-4" />
            Home
          </button>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#0F172A]">
            API Issue Logs
          </h1>
          <p className="text-sm text-[#64748B] mt-1 max-w-2xl">
            Inspect failed requests from production and other environments —
            payloads, error responses, and order context for debugging.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 self-start sm:self-auto rounded-xl border border-[#CBD5E1] bg-white px-3 py-2 text-sm font-semibold text-[#334155] hover:bg-[#F8FAFC]"
        >
          <Icon icon="solar:refresh-linear" className="!h-4 !w-4" />
          Refresh
          <span className="ml-1 rounded-md bg-[#DBEAFE] px-1.5 py-0.5 text-[11px] font-bold text-[#1D4ED8]">
            {meta.total}
          </span>
        </button>
      </div>

      <div className="mb-4 flex flex-col gap-3">
        <form onSubmit={applySearch} className="flex gap-2">
          <div className="relative flex-1">
            <Icon
              icon="solar:magnifer-linear"
              className="pointer-events-none absolute left-3 top-1/2 !h-4 !w-4 -translate-y-1/2 text-[#94A3B8]"
            />
            <input
              type="search"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              placeholder="Search endpoint, page, order id, status code…"
              className="h-11 w-full rounded-xl border border-[#D0D7E2] bg-white pl-10 pr-3 text-sm text-[#0F172A] outline-none transition placeholder:text-[#94A3B8] focus:border-primary1 focus:ring-2 focus:ring-[#1E61DC]/15"
            />
          </div>
          <button
            type="submit"
            className="h-11 shrink-0 rounded-xl bg-primary1 px-5 text-sm font-semibold text-white transition hover:brightness-110"
          >
            Search
          </button>
        </form>

        <div className="flex gap-1 overflow-x-auto rounded-xl bg-[#E8EEF8] p-1">
          {ENV_TABS.map((tab) => {
            const active = environment === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => selectEnvironment(tab.value)}
                className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  active
                    ? "bg-white text-primary1 shadow-sm"
                    : "text-[#64748B] hover:text-[#0F172A]"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-[#E2E8F0] bg-white shadow-sm overflow-hidden flex-1">
        {loading ? (
          <div className="flex justify-center py-20 text-[#64748B] text-sm">
            Loading issues…
          </div>
        ) : error ? (
          <div className="m-6 rounded-xl border border-[#FED7AA] bg-[#FFF7ED] px-4 py-3 text-sm text-[#9A3412]">
            {error}
          </div>
        ) : issues.length === 0 ? (
          <div className="py-20 text-center text-[#64748B]">
            No issues match these filters.
          </div>
        ) : (
          <>
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-[#F8FAFC] text-[11px] uppercase tracking-wider text-[#64748B]">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Created</th>
                    <th className="px-3 py-3 font-semibold">Code</th>
                    <th className="px-3 py-3 font-semibold">Status</th>
                    <th className="px-3 py-3 font-semibold">Env</th>
                    <th className="px-3 py-3 font-semibold">Endpoint</th>
                    <th className="px-3 py-3 font-semibold">Page</th>
                    <th className="px-3 py-3 font-semibold">Type</th>
                    <th className="px-3 py-3 font-semibold">Order</th>
                    <th className="px-5 py-3 font-semibold">User</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EEF2F7]">
                  {issues.map((issue) => {
                    const code = statusCodeTone(issue.statusCode);
                    const env = environmentTone(issue.environment);
                    const st = statusTone(issue.status);
                    return (
                      <tr
                        key={issue._id}
                        onClick={() => openIssue(issue._id)}
                        className="cursor-pointer hover:bg-[#F8FBFF] transition"
                      >
                        <td className="px-5 py-3 whitespace-nowrap text-[#475569]">
                          <div>
                            {moment(issue.createdAt).format("MMM D, HH:mm")}
                          </div>
                          <div className="text-xs text-[#94A3B8]">
                            {moment(issue.createdAt).fromNow()}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <LoggerBadge {...code}>{issue.statusCode}</LoggerBadge>
                        </td>
                        <td className="px-3 py-3">
                          <LoggerBadge {...st}>{st.label}</LoggerBadge>
                        </td>
                        <td className="px-3 py-3">
                          <LoggerBadge {...env}>{env.label}</LoggerBadge>
                        </td>
                        <td className="px-3 py-3 max-w-[260px]">
                          <div className="font-medium text-[#0F172A]">
                            <span className="text-[#64748B] mr-1">
                              {issue.method}
                            </span>
                            {truncate(issue.endpointCalled, 40)}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-[#334155]">
                          {truncate(issue.page, 28)}
                        </td>
                        <td className="px-3 py-3 text-[#334155]">
                          {issue.orderType}
                        </td>
                        <td className="px-3 py-3 text-[#334155]">
                          {orderLabel(issue.order)}
                        </td>
                        <td className="px-5 py-3 text-[#475569]">
                          {issue.user?.role || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="lg:hidden flex flex-col gap-3 p-4">
              {issues.map((issue) => {
                const code = statusCodeTone(issue.statusCode);
                const env = environmentTone(issue.environment);
                const st = statusTone(issue.status);
                return (
                  <button
                    key={issue._id}
                    type="button"
                    onClick={() => openIssue(issue._id)}
                    className="rounded-xl border border-[#E8EEF8] bg-[#F8FAFC] p-3 text-left"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-wrap gap-1.5">
                        <LoggerBadge {...code}>{issue.statusCode}</LoggerBadge>
                        <LoggerBadge {...st}>{st.label}</LoggerBadge>
                        <LoggerBadge {...env}>{env.label}</LoggerBadge>
                      </div>
                      <span className="text-xs text-[#64748B] whitespace-nowrap">
                        {moment(issue.createdAt).fromNow()}
                      </span>
                    </div>
                    <div className="mt-2 text-sm font-semibold text-[#0F172A]">
                      {issue.method} {truncate(issue.endpointCalled, 48)}
                    </div>
                    <div className="mt-1 text-xs text-[#64748B]">
                      {issue.page} · {issue.orderType} ·{" "}
                      {orderLabel(issue.order)}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {meta.totalPages > 1 ? (
          <div className="flex items-center justify-center gap-2 py-4 border-t border-[#E8EEF8]">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-[#CBD5E1] px-3 py-1.5 text-sm font-semibold text-[#334155] disabled:opacity-40 hover:bg-[#F8FAFC]"
            >
              Prev
            </button>
            <span className="text-sm text-[#64748B]">
              Page {page} of {meta.totalPages}
            </span>
            <button
              type="button"
              disabled={page >= meta.totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border border-[#CBD5E1] px-3 py-1.5 text-sm font-semibold text-[#334155] disabled:opacity-40 hover:bg-[#F8FAFC]"
            >
              Next
            </button>
          </div>
        ) : null}
      </div>

      <IssueDetailDrawer
        open={Boolean(selectedId)}
        issueId={selectedId}
        onClose={closeIssue}
        onChanged={() => load()}
      />
    </div>
  );
}
